import { Network } from '@capacitor/network'
import {
  getPendingVisits,
  removePendingVisit,
  updatePendingVisit,
  getPendingVisitCount,
  getRejectedVisits,
  clearLocalVisitStatuses,
  getPlacementFlips,
  updatePlacementFlip,
  removePlacementFlip,
  getRouteOutcomes,
  updateRouteOutcome,
  getPendingCalls,
  updatePendingCall,
  removePendingCall,
  markCachedCallRow,
} from './offlineStore'
import type { PendingCall, PendingVisit, QueuedRouteOutcome } from './offlineStore'
import type { RouteOutcomeResult } from '../types/canvasser'
import type { RecordCallResult } from '../types/followUp'
import { canvasserApi } from './api'
import { heldBehindFlip, isFlipOutstanding } from './flipOrder'
import { readGrantMirror } from './grantMirror'

/**
 * [R5] Route outcomes sync under the current grant_session cookie, so only the current grant's are
 * sent or counted here. A legacy entry (no grantId stamp) is treated as the current grant's.
 */
function isCurrentGrantOutcome(o: QueuedRouteOutcome): boolean {
  return !o.grantId || o.grantId === readGrantMirror()?.grantId
}

/** [CF-1 P4 / R4] Same rule for queued calls: only the current grant's are sent or counted. */
function isCurrentGrantCall(c: PendingCall): boolean {
  return !c.grantId || c.grantId === readGrantMirror()?.grantId
}

// ─── Types ───────────────────────────────────────────────────────

export type SyncState = 'idle' | 'syncing' | 'error'

export interface SyncStatus {
  isOnline: boolean
  syncState: SyncState
  pendingCount: number
  lastSyncError?: string
  /** [C2] Visits the server terminally rejected. Kept, never retried, shown to the canvasser. */
  rejectedCount: number
  /** One short line per rejected visit, carrying the SERVER's own reason. */
  rejectedSummaries: string[]
}

type StatusListener = (status: SyncStatus) => void

// ─── Constants ───────────────────────────────────────────────────

const MAX_RETRIES = 5
const BATCH_SIZE = 25

// ─── Network error detection ────────────────────────────────────

export function isNetworkError(err: unknown): boolean {
  if (!navigator.onLine) return true
  if (err instanceof Error && err.message === 'Network Error') return true
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as Record<string, unknown>).code
    if (code === 'ERR_NETWORK' || code === 'ECONNABORTED') return true
  }
  return false
}

/** The server's own message, never a generic retry line (frontend/src/CLAUDE.md; roadmap A10). */
export function serverMessage(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data
    if (typeof data === 'object' && data !== null && 'message' in data) {
      const m = (data as { message?: unknown }).message
      if (typeof m === 'string' && m.length > 0) return m
    }
  }
  return err instanceof Error ? err.message : fallback
}

/** A 400 from the canvassing write paths is the scope gate's uniform client-input rejection. */
function isRejection(err: unknown): boolean {
  return typeof err === 'object' && err !== null && 'response' in err
    && (err as { response?: { status?: number } }).response?.status === 400
}

// ─── Sync Engine ─────────────────────────────────────────────────

class SyncEngine {
  private status: SyncStatus = {
    isOnline: navigator.onLine,
    syncState: 'idle',
    pendingCount: 0,
    rejectedCount: 0,
    rejectedSummaries: [],
  }
  private listeners = new Set<StatusListener>()
  private networkListenerHandle: { remove: () => void } | null = null
  private syncInProgress = false
  private initialized = false

  subscribe(listener: StatusListener): () => void {
    this.listeners.add(listener)
    listener({ ...this.status })
    return () => this.listeners.delete(listener)
  }

  private emit() {
    const snapshot = { ...this.status }
    for (const listener of this.listeners) {
      listener(snapshot)
    }
  }

  async init(): Promise<void> {
    if (this.initialized) return
    this.initialized = true

    // Get initial network status
    try {
      const netStatus = await Network.getStatus()
      this.status.isOnline = netStatus.connected
    } catch {
      this.status.isOnline = navigator.onLine
    }

    // Get pending count
    this.status.pendingCount = await this.countPending()
    await this.refreshRejected()
    this.emit()

    // Listen for network changes
    try {
      this.networkListenerHandle = await Network.addListener(
        'networkStatusChange',
        async (netStatus) => {
          const wasOffline = !this.status.isOnline
          this.status.isOnline = netStatus.connected
          this.emit()

          // Auto-sync when coming back online. With no visits pending, a flip whose visit already
          // synced (but whose own send hit the network) still needs draining (final-review I-1).
          if (wasOffline && netStatus.connected) {
            if (this.status.pendingCount > 0) await this.sync()
            else {
              await this.drainFlips().catch(() => {})
              await this.drainRouteOutcomes().catch(() => {})
              await this.drainCalls().catch(() => {})
            }
          }
        }
      )
    } catch {
      // Fallback: use browser online/offline events
      const onOnline = () => {
        const wasOffline = !this.status.isOnline
        this.status.isOnline = true
        this.emit()
        if (wasOffline) {
          if (this.status.pendingCount > 0) void this.sync()
          else {
            void this.drainFlips().catch(() => {})
              .then(() => this.drainRouteOutcomes()).catch(() => {})
              .then(() => this.drainCalls()).catch(() => {})
          }
        }
      }
      const onOffline = () => {
        this.status.isOnline = false
        this.emit()
      }
      window.addEventListener('online', onOnline)
      window.addEventListener('offline', onOffline)
      this.networkListenerHandle = {
        remove: () => {
          window.removeEventListener('online', onOnline)
          window.removeEventListener('offline', onOffline)
        },
      }
    }
  }

  async refreshPendingCount(): Promise<void> {
    this.status.pendingCount = await this.countPending()
    await this.refreshRejected()
    this.emit()
  }

  /**
   * Queued visits plus the current grant's route outcomes and calls still owed to the server (rejected
   * ones included, like visits). Public so SetAsideGrantSync can gate on visits, drop-offs AND calls (R5/R4).
   */
  async countPending(): Promise<number> {
    const outcomes = await getRouteOutcomes()
    const calls = await getPendingCalls()
    return (await getPendingVisitCount())
      + outcomes.filter(o => !o.synced && isCurrentGrantOutcome(o)).length
      + calls.filter(c => isCurrentGrantCall(c.entry)).length
  }

  private async refreshRejected(): Promise<void> {
    const rejected = await getRejectedVisits()
    const rejectedOutcomes = (await getRouteOutcomes()).filter(o => o.rejected === true && isCurrentGrantOutcome(o))
    const rejectedCalls = (await getPendingCalls()).filter(c => c.entry.rejected === true && isCurrentGrantCall(c.entry))
    this.status.rejectedCount = rejected.length + rejectedOutcomes.length + rejectedCalls.length
    this.status.rejectedSummaries = [
      ...rejected.map(r => {
        const v = r.entry.visit as { contactId?: string }
        return `${v.contactId ?? 'A door'} — ${r.entry.rejectedReason ?? 'could not be saved'}`
      }),
      ...rejectedOutcomes.map(o => `A drop-off — ${o.rejectedReason ?? 'could not be saved'}`),
      ...rejectedCalls.map(c => `A call — ${c.entry.rejectedReason ?? 'could not be saved'}`),
    ]
  }

  /**
   * [C2 finding 10] Replay a rejected batch ONE VISIT AT A TIME through record-visit, so a single
   * out-of-scope door cannot strand the 24 good ones queued beside it. A 400 on the individual
   * replay is TERMINAL: the entry is marked rejected (kept, excluded from auto-retry, surfaced);
   * anything else is an ordinary retryable failure.
   */
  private async replayIndividually(
    batch: Array<{ id: number; entry: PendingVisit }>
  ): Promise<void> {
    for (const item of batch) {
      try {
        await canvasserApi.recordVisit(item.entry.visit)
        await removePendingVisit(item.id)
      } catch (err) {
        if (isRejection(err)) {
          await updatePendingVisit(item.id, {
            rejected: true,
            rejectedReason: serverMessage(err, 'This visit could not be saved'),
          })
        } else {
          await updatePendingVisit(item.id, {
            retryCount: item.entry.retryCount + 1,
            lastError: serverMessage(err, 'Network error'),
          })
        }
      }
    }
  }

  /**
   * [P4-S3] Send queued "Visit at home today" flips. A flip waits until the NotAtWork visit it follows
   * has left the queue (synced), because the visit's WorksiteId stamp needs the member still placed at
   * work. A flip whose visit was rejected is dropped (that door never recorded). Only a 400 (scope gate:
   * not eligible / not on today's turf) or a 404 is terminal: dropped. Everything else (network, 401/403
   * mid-refresh, 408, 429, 5xx) retries up to FLIP_MAX_RETRIES, mirroring the visit path's isRejection.
   */
  async drainFlips(): Promise<void> {
    const flips = (await getPlacementFlips()).filter(isFlipOutstanding)
    if (flips.length === 0) return
    const pending = await getPendingVisits()
    for (const f of flips) {
      const visit = pending.find(p => p.entry.visit.clientVisitId === f.afterClientVisitId)
      if (visit?.entry.rejected) { await removePlacementFlip(f.turfMemberId); continue }
      if (visit) continue // still waiting for its visit
      try {
        await canvasserApi.visitAtHome(f.turfMemberId)
        await updatePlacementFlip(f.turfMemberId, { synced: true })
      } catch (err) {
        const status = (err as { response?: { status?: number } } | null)?.response?.status
        if (status === 400 || status === 404) await removePlacementFlip(f.turfMemberId)
        else await updatePlacementFlip(f.turfMemberId, { retryCount: f.retryCount + 1 })
      }
    }
  }

  /**
   * [CF-1 P3] Send queued action-route outcomes in batches of BATCH_SIZE. Per item: success / duplicate
   * -> synced; rejected (also read off the all-rejected 400's results) -> kept with the server's message,
   * never retried; anything else -> retryCount + 1 (stops being sent at MAX_RETRIES, but is kept).
   * [R5] Only the current grant's outcomes (and legacy unstamped ones) are sent.
   */
  async drainRouteOutcomes(): Promise<void> {
    const todo = (await getRouteOutcomes())
      .filter(o => !o.synced && o.rejected !== true && o.retryCount < MAX_RETRIES && isCurrentGrantOutcome(o))
    for (let i = 0; i < todo.length; i += BATCH_SIZE) {
      const batch = todo.slice(i, i + BATCH_SIZE)
      let results: RouteOutcomeResult[] | null = null
      try {
        results = (await canvasserApi.recordRouteOutcomes(batch.map(b => b.outcome))).data.results
      } catch (err) {
        const res = (err as { response?: { status?: number; data?: { results?: unknown } } } | null)?.response
        if (res?.status === 400 && Array.isArray(res.data?.results)) results = res.data!.results as RouteOutcomeResult[]
        else {
          for (const b of batch) await updateRouteOutcome(b.clientOutcomeId, { retryCount: b.retryCount + 1 })
          continue
        }
      }
      for (const b of batch) {
        const r = results.find(x => x.clientOutcomeId === b.clientOutcomeId)
        await this.settleOutcome(b, r)
      }
    }
  }

  private async settleOutcome(b: QueuedRouteOutcome, r: RouteOutcomeResult | undefined): Promise<void> {
    if (r && (r.success || r.duplicate)) await updateRouteOutcome(b.clientOutcomeId, { synced: true })
    else if (r?.rejected) {
      await updateRouteOutcome(b.clientOutcomeId, { rejected: true, rejectedReason: r.error ?? 'This drop-off could not be saved' })
    } else await updateRouteOutcome(b.clientOutcomeId, { retryCount: b.retryCount + 1 })
  }

  /**
   * [CF-1 P4] Send queued calls in batches of BATCH_SIZE (well under the served MaxBatchCalls). Per
   * result: success -> removed; rejected -> kept with the server's message, never retried, surfaced
   * (e.g. "This call list is no longer assigned to you" after a reassign — Review Focus 2); Success =
   * false -> retryCount + 1. A whole-request 400 (no per-item results) replays each call alone, and a
   * 400 on a single call is terminal, like visits. A network error spends a retry; MAX_RETRIES stops
   * sending but keeps the entry. [R4] Only the current grant's calls are sent.
   */
  async drainCalls(): Promise<void> {
    const todo = (await getPendingCalls())
      .filter(c => c.entry.rejected !== true && c.entry.retryCount < MAX_RETRIES && isCurrentGrantCall(c.entry))
    for (let i = 0; i < todo.length; i += BATCH_SIZE) {
      await this.sendCalls(todo.slice(i, i + BATCH_SIZE))
    }
  }

  private async sendCalls(batch: Array<{ id: number; entry: PendingCall }>): Promise<void> {
    let results: RecordCallResult[]
    try {
      // [CF-1 P5] Each call names the batch it was queued from (a chase item can sit in several).
      results = (await canvasserApi.recordCalls(batch.map(b => ({ ...b.entry.call, callBatchId: b.entry.batchId })))).data.results
    } catch (err) {
      const res = (err as { response?: { status?: number; data?: { results?: unknown } } } | null)?.response
      if (res?.status === 400 && Array.isArray(res.data?.results)) results = res.data!.results as RecordCallResult[]
      else if (isRejection(err) && batch.length > 1) {
        for (const b of batch) await this.sendCalls([b])
        return
      } else if (isRejection(err)) {
        await updatePendingCall(batch[0].id, { rejected: true, rejectedReason: serverMessage(err, 'This call could not be saved') })
        return
      } else {
        for (const b of batch) {
          await updatePendingCall(b.id, { retryCount: b.entry.retryCount + 1, lastError: serverMessage(err, 'Network error') })
        }
        return
      }
    }
    for (const b of batch) {
      const r = results.find(x => x.clientCallId === b.entry.call.clientCallId)
      if (r?.success) {
        // Patch the cached batch first: once the pending entry is gone, it's the only trace of the call.
        await markCachedCallRow(b.entry.batchId, b.entry.call.itemId, {
          outcome: b.entry.call.outcome, itemStatus: r.itemStatus, markDoNotContact: b.entry.call.markDoNotContact,
        }).catch(() => {})
        await removePendingCall(b.id)
      }
      else if (r?.rejected) {
        await updatePendingCall(b.id, { rejected: true, rejectedReason: r.error ?? 'This call could not be saved' })
      } else {
        await updatePendingCall(b.id, { retryCount: b.entry.retryCount + 1, lastError: r?.error ?? 'Unknown error' })
      }
    }
  }

  /** Send queued visits in batches of BATCH_SIZE; per-item results drive remove / reject / retry. */
  private async sendVisits(visits: Array<{ id: number; entry: PendingVisit }>): Promise<void> {
    for (let i = 0; i < visits.length; i += BATCH_SIZE) {
      const batch = visits.slice(i, i + BATCH_SIZE)
      const retryable = batch.filter(b => b.entry.retryCount < MAX_RETRIES)
      if (retryable.length === 0) continue

      try {
        const response = await canvasserApi.batchRecordVisits({
          visits: retryable.map(b => b.entry.visit),
        })
        const results = response.data.results

        for (let j = 0; j < results.length; j++) {
          const result = results[j]
          const item = retryable[j]
          if (result.success) {
            await removePendingVisit(item.id)
          } else if (result.rejected) {
            await updatePendingVisit(item.id, {
              rejected: true,
              rejectedReason: result.error ?? 'This visit could not be saved',
            })
          } else {
            await updatePendingVisit(item.id, {
              retryCount: item.entry.retryCount + 1,
              lastError: result.error ?? 'Unknown error',
            })
          }
        }
      } catch (err) {
        if (isRejection(err)) {
          await this.replayIndividually(retryable)
        } else {
          for (const item of retryable) {
            await updatePendingVisit(item.id, {
              retryCount: item.entry.retryCount + 1,
              lastError: serverMessage(err, 'Network error'),
            })
          }
        }
      }
    }
  }

  async sync(): Promise<void> {
    if (this.syncInProgress || !this.status.isOnline) return

    const all = await getPendingVisits()
    // Rejected entries are TERMINAL: never re-sent, never dropped.
    const pending = all.filter(p => p.entry.rejected !== true)
    if (pending.length === 0) {
      try { await this.drainFlips() } catch { /* a flip failure never fails the visit sync */ }
      try { await this.drainRouteOutcomes() } catch { /* an outcome failure never fails the visit sync */ }
      try { await this.drainCalls() } catch { /* a call failure never fails the visit sync */ }
      await this.refreshRejected()
      this.status.pendingCount = await this.countPending()
      this.status.syncState = this.status.rejectedCount > 0 ? 'error' : 'idle'
      this.status.lastSyncError = undefined
      this.emit()
      return
    }

    this.syncInProgress = true
    this.status.syncState = 'syncing'
    this.emit()

    try {
      // [Final-review I-3] A visit for someone with an unsynced flip waits until the flip lands (the
      // server stamps WorksiteId from the CURRENT placement). The flip's own NotAtWork visit goes first.
      const held = heldBehindFlip(await getPlacementFlips())
      await this.sendVisits(pending.filter(p => !held(p.entry.visit)))
      await this.refreshRejected()
      try { await this.drainFlips() } catch { /* a flip failure never fails the visit sync */ }
      const heldBack = pending.filter(p => held(p.entry.visit))
      if (heldBack.length > 0) {
        // Re-read: flips that drained just now release their person's visits in this same run; the rest
        // stay queued, untouched (no retry count spent), for the next run.
        const stillHeld = heldBehindFlip(await getPlacementFlips())
        await this.sendVisits(heldBack.filter(p => !stillHeld(p.entry.visit)))
        await this.refreshRejected()
      }
      try { await this.drainRouteOutcomes() } catch { /* an outcome failure never fails the visit sync */ }
      try { await this.drainCalls() } catch { /* a call failure never fails the visit sync */ }
      await this.refreshRejected()
      const remaining = await this.countPending()
      const outstanding = remaining - this.status.rejectedCount
      this.status.pendingCount = remaining
      this.status.syncState =
        this.status.rejectedCount > 0 || outstanding > 0 ? 'error' : 'idle'
      // Visits and drop-offs both count here, so the line names neither (m1).
      this.status.lastSyncError =
        outstanding > 0 ? 'Some changes failed to sync' : undefined

      // Clear the contactId-keyed door-visit overrides once every VISIT has left the queue (synced;
      // a rejected one stays and keeps them). Route outcomes don't gate this: they have their own
      // turf-keyed overlay, and a rejected drop-off would otherwise pin door overrides forever (m1).
      if ((await getPendingVisitCount()) === 0) {
        await clearLocalVisitStatuses()
      }
    } catch (err) {
      this.status.syncState = 'error'
      this.status.lastSyncError = serverMessage(err, 'Sync failed')
    } finally {
      this.syncInProgress = false
      this.emit()
    }
  }

  getStatus(): SyncStatus {
    return { ...this.status }
  }

  destroy(): void {
    if (this.networkListenerHandle) {
      this.networkListenerHandle.remove()
      this.networkListenerHandle = null
    }
    this.listeners.clear()
    this.initialized = false
  }
}

// Singleton instance
export const syncEngine = new SyncEngine()
